// What a child may see of the trip right now (docs/ORGANISER_PLAN.md §5; mockups o07, o11).
// The answer is the same Pack shape with things masked, so the Map, Trip, Now card and Crate
// all respect it without knowing about settings:
//   · a surprise item shows its teaser, with no place or note;
//   · a hidden stage becomes a "mystery" stage (spot `mystery`, a wrapped island) with a reveal
//     date, and its plans, legs' names and places are left out;
//   · "today + N days" keeps every stage's name but leaves out plans beyond the window.
// It's a curtain, not a vault: the phone holds the whole trip so reveals work offline (§3.5).
import type { Visibility } from './controls'
import type { DayItem, Leg, Pack, Stage } from './pack/schema'
import { addDays } from './time'

export interface TripClock {
  /** Today in the trip timezone (YYYY-MM-DD). */
  date: string
  /** Now in the trip timezone (HH:MM). */
  time: string
}

export const MYSTERY_SPOT = 'mystery'
const MYSTERY_NAME = 'Mystery stage'

/** A surprise's reveal ("2027-01-13" or "2027-01-13T18:00") has passed. */
export function revealed(until: string, now: TripClock): boolean {
  const [date, time = '00:00'] = until.split('T') as [string, string?]
  return date < now.date || (date === now.date && time <= now.time)
}

/**
 * How long until a surprise opens: whole days before its day, then minutes on the day itself
 * (for the countdown on a wrapped stage, BUILD_PLAN_NEXT §5).
 */
export function opensIn(until: string, now: TripClock): { days: number; minutes: number | null } {
  const [date, time = '00:00'] = until.split('T') as [string, string?]
  const days = Math.max(0, Math.round((Date.parse(`${date}T00:00:00Z`) - Date.parse(`${now.date}T00:00:00Z`)) / 86_400_000))
  if (days > 0) return { days, minutes: null }
  const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5))
  return { days: 0, minutes: Math.max(0, toMin(time) - toMin(now.time)) }
}

/** The date each stage opens to this child, or null when it's never hidden. */
export function stageReveals(stages: readonly Stage[], v: Visibility): Map<string, string | null> {
  return new Map(
    stages.map((s, i) => {
      const own = s.surprise?.until.slice(0, 10) ?? null
      if (v.mode === 'stage') return [s.id, i === 0 ? own : s.startDate]
      if (v.mode === 'mystery') return [s.id, v.reveals[s.id] ?? (i === 0 ? own : s.startDate)]
      return [s.id, own]
    }),
  )
}

const day = (iso: string) => iso.slice(0, 10)

export function visibleTrip(pack: Pack, v: Visibility, now: TripClock): Pack {
  const { stages, legs, dayItems } = pack.itinerary
  const opens = stageReveals(stages, v)
  const hidden = new Set(
    stages
      .filter((s) => {
        const r = opens.get(s.id)
        return !!r && !revealed(r, now)
      })
      .map((s) => s.id),
  )
  const windowEnd = v.mode === 'window' ? addDays(now.date, v.days) : null
  const stageOf = (date: string) => stages.find((s) => s.startDate <= date && date <= s.endDate)
  const inHidden = (date: string) => {
    const s = stageOf(date)
    return !!s && hidden.has(s.id)
  }

  if (!hidden.size && !windowEnd && !dayItems.some((i) => i.surprise && !revealed(i.surprise.until, now))) return pack

  const maskedStages = stages.map((s): Stage => {
    if (!hidden.has(s.id)) return s
    const teaser = s.surprise?.teaser ?? MYSTERY_NAME
    // The region goes too: it would name the place through its weather, places and island.
    return { ...s, name: teaser, shortName: '?', spot: MYSTERY_SPOT, regionId: MYSTERY_SPOT, surprise: { until: opens.get(s.id)!, teaser, ...(s.surprise?.clue ? { clue: s.surprise.clue } : {}) } }
  })

  const items = dayItems
    .filter((i) => !inHidden(i.date) && (!windowEnd || i.date <= windowEnd))
    .map((i): DayItem => {
      if (!i.surprise || revealed(i.surprise.until, now)) return i
      const { sub: _sub, placeId: _place, ...rest } = i
      return { ...rest, title: i.surprise.teaser }
    })

  // A leg into or out of a hidden stage would give the place away. A leg on a stage's first day
  // leaves from the stage before it (stages are contiguous: you travel on the new stage's day 1).
  const leavingFrom = (date: string) => {
    const s = stageOf(date)
    return s && s.startDate === date ? stageOf(addDays(date, -1)) : s
  }
  const masked = (stop: Leg['from']) => ({ ...stop, code: '?', name: '???' })
  // Notes are free text ("via Otaru"), so while anything is hidden no leg keeps its note.
  const maskedLegs = legs.map((l): Leg => {
    const fromHidden = hidden.has(leavingFrom(day(l.departsAt))?.id ?? '')
    const toHidden = hidden.has(stageOf(day(l.arrivesAt))?.id ?? '')
    if (!fromHidden && !toHidden) {
      if (!hidden.size || l.notes === undefined) return l
      const { notes: _notes, ...kept } = l
      return kept
    }
    const { ref: _ref, notes: _notes, ...rest } = l
    return { ...rest, from: fromHidden ? masked(l.from) : l.from, to: toHidden ? masked(l.to) : l.to }
  })

  // Places only a hidden stage visits stay out of the Crate and the Map until it opens.
  const shownRegions = new Set(maskedStages.filter((s) => !hidden.has(s.id)).map((s) => s.regionId))
  const hiddenRegions = new Set(stages.filter((s) => hidden.has(s.id)).map((s) => s.regionId))
  const places = pack.places.filter((p) => !hiddenRegions.has(p.regionId) || shownRegions.has(p.regionId))

  return { ...pack, itinerary: { stages: maskedStages, legs: maskedLegs, dayItems: items }, places }
}
